import { Badge, Box, HStack, Icon, IconButton, Image, Text, VStack } from '@chakra-ui/react';
import {
  ADVISOR_PANEL_NEUTRAL_100,
  ADVISOR_PANEL_NEUTRAL_250,
  ADVISOR_PANEL_PRIMARY_500,
  AgentAssistantMessageRow,
  AgentComposeBar,
  AgentMessageThreadScroll,
  AgentPanelHeaderIconButton,
  AgentScrollToLatestPill,
  AgentSidePanelFrame,
  type AgentSubmitMessageFeedbackFn,
  AgentSuggestionChip,
  AgentSystemErrorMessageRow,
  AgentThinkingIndicator,
  AgentThreadHistoryView,
  AgentUserMessageBubble,
  agentV2HistoryRowsToUiMessages,
  assistantFooterStateKey,
  createAgentAssistantMessageFeedbackOnError,
  type DataSuggestionOption,
  fileListFromFiles,
  getDataSuggestionsOptions,
  getFollowUpsAfterLastUserMessage,
  getUserMessageFilePartFilenames,
  mergeAgentV2HistoryRowsIntoUiMessages,
  resolveAdvisorThreadPresentation,
  useAdminAdvisorEmptySuggestionTexts,
  useAdvisorMessagesScroll,
  useAdvisorPendingReportDeliveryPoll,
  useAgentChatAttachments,
} from '@kintsugi/advisor-ui';
import { Tooltip } from '@kintsugi/ui';
import { useAuthInfo } from '@propelauth/react';
import type { UIMessage } from 'ai';
import { CloseButton } from '@/components/ui/close-button';
import { toaster } from '@/components/ui/toaster-instance';
import {
  ADMIN_USE_CASE,
  buildAdminAgentAuthHeaders,
  fetchAdminAgentThreadMessages,
  getMessageDisplayText,
  useAdminAgentChat,
  useAdminAgentV2ThreadHistory,
} from '@/hooks/useAdminAgentChat';
import { adminAgentFriendlyErrorMessage } from '@/utils/adminAgentErrors';
import { RefreshCw } from 'lucide-react';
import {
  type Dispatch,
  memo,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { useAdminAgent } from './use-admin-agent';
import {
  KIN_CHAT_ICON_URL,
  KIN_CLOSE_URL,
  KIN_HISTORY_URL,
  KIN_NEW_CHAT_URL,
  KIN_SEND_URL,
  KIN_UPLOAD_URL,
} from './kin-icons';

const KIN_ACCENT = ADVISOR_PANEL_PRIMARY_500;

const handleAdminMessageFeedbackError = createAgentAssistantMessageFeedbackOnError(p => toaster.create(p));

interface KinIconImageProps {
  src: string;
  size?: string;
}

function KinIconImage({ src, size = '32px' }: KinIconImageProps) {
  return <Image src={src} alt="" boxSize={size} flexShrink={0} draggable={false} aria-hidden />;
}

function AssistantHeader({
  scope,
  organizationName,
  organizationId,
  onNewChat,
  onOpenHistory,
  historyDisabled,
  onClose,
}: {
  scope: string;
  organizationName?: string;
  organizationId?: string;
  onNewChat: () => void;
  onOpenHistory: () => void;
  historyDisabled?: boolean;
  onClose: () => void;
}) {
  const isOrg = scope === 'organization';
  return (
    <HStack
      px={4}
      h="64px"
      minH="64px"
      borderBottomWidth="1px"
      borderBottomColor={ADVISOR_PANEL_NEUTRAL_100}
      justify="space-between"
      bg="white"
      flexShrink={0}
    >
      <HStack gap={3}>
        <KinIconImage src={KIN_CHAT_ICON_URL} size="34px" />
        <VStack align="start" gap={0}>
          <Text fontWeight="medium" fontSize="14px" lineHeight="20px" color="black">
            Kin
          </Text>
          {isOrg ? (
            <Badge size="sm" colorPalette="blue" variant="subtle" fontSize="2xs">
              {organizationName || organizationId}
            </Badge>
          ) : (
            <Text fontSize="2xs" color={ADVISOR_PANEL_NEUTRAL_250} lineHeight="short">
              Admin portfolio
            </Text>
          )}
        </VStack>
      </HStack>
      <HStack gap={2}>
        <Tooltip content="Chat history">
          <AgentPanelHeaderIconButton aria-label="Chat history" onClick={onOpenHistory} disabled={historyDisabled}>
            <KinIconImage src={KIN_HISTORY_URL} size="18px" />
          </AgentPanelHeaderIconButton>
        </Tooltip>
        <Tooltip content="New Chat">
          <AgentPanelHeaderIconButton aria-label="New Chat" onClick={onNewChat}>
            <KinIconImage src={KIN_NEW_CHAT_URL} size="18px" />
          </AgentPanelHeaderIconButton>
        </Tooltip>
        <Tooltip content="Close">
          <CloseButton
            size="rg"
            w="30px"
            minW="30px"
            h="30px"
            borderRadius="4px"
            borderWidth="1px"
            borderColor={ADVISOR_PANEL_NEUTRAL_100}
            variant="outline"
            onClick={onClose}
          >
            <Image src={KIN_CLOSE_URL} alt="" boxSize={4} draggable={false} aria-hidden />
          </CloseButton>
        </Tooltip>
      </HStack>
    </HStack>
  );
}

function EmptyState({
  scope,
  organizationName,
  suggestions,
  suggestionsLoading,
  suggestionsError,
  onRefetchSuggestions,
  onSendSuggestion,
  suggestionsDisabled,
}: {
  scope: string;
  organizationName?: string;
  suggestions?: string[];
  suggestionsLoading?: boolean;
  suggestionsError?: boolean;
  onRefetchSuggestions?: () => void;
  onSendSuggestion: (option: { text: string; use_case?: string }) => void;
  suggestionsDisabled?: boolean;
}) {
  const chipList = suggestions ?? [];

  return (
    <VStack gap={8} py={8} px={2} flex={1} justify="center">
      <Box opacity={0.6}>
        <KinIconImage src={KIN_CHAT_ICON_URL} size="40px" />
      </Box>
      <VStack gap={2} align="stretch" w="100%">
        <Text fontSize="18px" fontWeight="medium" lineHeight="26px" color="black" textAlign="center">
          How can I help?
        </Text>
        <Text
          fontSize="12px"
          fontWeight="medium"
          lineHeight="18px"
          color={ADVISOR_PANEL_NEUTRAL_250}
          textAlign="center"
          maxW="280px"
          mx="auto"
        >
          {scope === 'portfolio'
            ? 'Ask about organizations across the admin panel, or open an organization page for org-specific help.'
            : `Ask about ${organizationName ?? 'this organization'} — filings, registrations, and nexus.`}
        </Text>
      </VStack>
      <VStack gap={2} w="100%" maxW="404px" pt={0} aria-busy={suggestionsLoading || undefined}>
        {suggestionsLoading ? (
          <Text fontSize="sm" color={ADVISOR_PANEL_NEUTRAL_250} textAlign="center">
            Loading suggestions…
          </Text>
        ) : null}
        {suggestionsError ? (
          <HStack justify="center" gap={2} flexWrap="wrap">
            <Text fontSize="sm" color="red.600" textAlign="center">
              Couldn&apos;t load suggestions.
            </Text>
            {onRefetchSuggestions ? (
              <IconButton
                type="button"
                variant="ghost"
                size="sm"
                aria-label="Retry loading suggestions"
                onClick={onRefetchSuggestions}
                color="red.600"
              >
                <Icon as={RefreshCw} boxSize={3.5} />
              </IconButton>
            ) : null}
          </HStack>
        ) : null}
        {chipList.map((s, i) => (
          <AgentSuggestionChip
            key={`${i}-${s}`}
            text={s}
            onSend={({ text }) => onSendSuggestion({ text })}
            disabled={suggestionsDisabled}
            hoverBorderColor={KIN_ACCENT}
            hoverTextColor={KIN_ACCENT}
          />
        ))}
      </VStack>
    </VStack>
  );
}

const AdminUserMessageRow = memo(
  function AdminUserMessageRow({ message }: { message: UIMessage }) {
    return (
      <AgentUserMessageBubble
        text={getMessageDisplayText(message)}
        attachmentFilenames={getUserMessageFilePartFilenames(message)}
      />
    );
  },
  (a, b) =>
    a.message.id === b.message.id &&
    getMessageDisplayText(a.message) === getMessageDisplayText(b.message) &&
    getUserMessageFilePartFilenames(a.message).join('|') === getUserMessageFilePartFilenames(b.message).join('|')
);

interface AdminAssistantMessageRowProps {
  message: UIMessage;
  isLast: boolean;
  busy: boolean;
  canUseAgent: boolean;
  onSendSuggestion: (option: { text: string; use_case?: string }) => void;
  submitMessageFeedback: AgentSubmitMessageFeedbackFn;
  setMessages: Dispatch<SetStateAction<UIMessage[]>>;
  writtenFeedbackMessageId: string | null;
  setWrittenFeedbackMessageId: Dispatch<SetStateAction<string | null>>;
  kinFeedbackSubmitPendingRef: MutableRefObject<boolean>;
  closeKinInlineFeedbackRef: MutableRefObject<(() => void) | null>;
  followUpOptionsOverride?: DataSuggestionOption[];
}

const AdminAssistantMessageRow = memo(
  function AdminAssistantMessageRow({
    message,
    isLast,
    busy,
    canUseAgent,
    onSendSuggestion,
    submitMessageFeedback,
    setMessages,
    writtenFeedbackMessageId,
    setWrittenFeedbackMessageId,
    kinFeedbackSubmitPendingRef,
    closeKinInlineFeedbackRef,
    followUpOptionsOverride,
  }: AdminAssistantMessageRowProps) {
    return (
      <AgentAssistantMessageRow
        message={message}
        text={getMessageDisplayText(message)}
        isLast={isLast}
        busy={busy}
        canUseAgent={canUseAgent}
        accentColor={KIN_ACCENT}
        onSendSuggestion={onSendSuggestion}
        submitMessageFeedback={submitMessageFeedback}
        setMessages={setMessages}
        writtenFeedbackMessageId={writtenFeedbackMessageId}
        setWrittenFeedbackMessageId={setWrittenFeedbackMessageId}
        kinFeedbackSubmitPendingRef={kinFeedbackSubmitPendingRef}
        closeKinInlineFeedbackRef={closeKinInlineFeedbackRef}
        onFeedbackError={handleAdminMessageFeedbackError}
        followUpOptionsOverride={followUpOptionsOverride}
        footerStateKey={assistantFooterStateKey(message, false, followUpOptionsOverride)}
      />
    );
  },
  (a, b) => {
    if (a.message !== b.message) return false;
    return (
      a.isLast === b.isLast &&
      a.busy === b.busy &&
      a.canUseAgent === b.canUseAgent &&
      a.onSendSuggestion === b.onSendSuggestion &&
      a.submitMessageFeedback === b.submitMessageFeedback &&
      a.setMessages === b.setMessages &&
      a.writtenFeedbackMessageId === b.writtenFeedbackMessageId &&
      a.setWrittenFeedbackMessageId === b.setWrittenFeedbackMessageId &&
      a.kinFeedbackSubmitPendingRef === b.kinFeedbackSubmitPendingRef &&
      a.closeKinInlineFeedbackRef === b.closeKinInlineFeedbackRef &&
      a.followUpOptionsOverride === b.followUpOptionsOverride &&
      a.message.id === b.message.id &&
      getMessageDisplayText(a.message) === getMessageDisplayText(b.message) &&
      assistantFooterStateKey(a.message, false, a.followUpOptionsOverride) ===
        assistantFooterStateKey(b.message, false, b.followUpOptionsOverride)
    );
  }
);

export function AdminAdvisorPanel() {
  const location = useLocation();
  const { accessToken, loading: authLoading } = useAuthInfo();
  const { eligible, open, setOpen, scope, organizationId, organizationName } = useAdminAgent();

  const chatId = `admin-${scope}-${organizationId ?? 'portfolio'}`;

  const [input, setInput] = useState('');
  const latestInputRef = useRef(input);
  const setComposerInput = useCallback((value: string) => {
    latestInputRef.current = value;
    setInput(value);
  }, []);

  const {
    attachments: pendingAttachments,
    addFiles: addPendingAttachments,
    removeAt: removePendingAttachment,
    clear: clearPendingAttachments,
    errorMessages: pendingAttachmentErrors,
    clearErrors: clearPendingAttachmentErrors,
    isAtLimit: isPendingAttachmentLimit,
  } = useAgentChatAttachments();

  const sendSeqRef = useRef(0);
  const pendingSendRef = useRef<{ seq: number; text: string; files: File[] } | null>(null);

  const handleAdminAgentChatError = useCallback(() => {
    const pending = pendingSendRef.current;
    if (!pending) return;
    if (pending.seq !== sendSeqRef.current) return;
    if (!latestInputRef.current) {
      setComposerInput(pending.text);
    }
    if (pending.files.length > 0) {
      addPendingAttachments(pending.files);
    }
    pendingSendRef.current = null;
  }, [addPendingAttachments, setComposerInput]);

  const {
    messages,
    setMessages,
    sendMessage,
    regenerate,
    stop,
    status,
    error,
    agentThreadId,
    agentThreadIdRef: activeThreadIdRef,
    threadServerReady,
    clearThreadServerReady,
    streamIdleToken,
    beginNewAgentThread,
    selectPersistedThread,
    submitMessageFeedback,
    queueRouteUseCaseForNextStream,
  } = useAdminAgentChat({
    chatId,
    scope,
    organizationId,
    organizationName,
    pathname: location.pathname,
    onChatError: handleAdminAgentChatError,
  });

  useEffect(() => {
    if (error) return;
    if (status !== 'ready' && status !== 'streaming') return;
    if (!pendingSendRef.current?.files.length) return;
    pendingSendRef.current = null;
  }, [error, status]);

  const suggestionChips = useAdminAdvisorEmptySuggestionTexts(import.meta.env.VITE_API_URL, scope, organizationName, {
    enabled: open && eligible,
    getAuthHeaders: () => buildAdminAgentAuthHeaders(organizationId),
    cacheVersion: import.meta.env.VITE_AGENT_UI_TEMPLATES_VERSION,
    cacheScopeKey: scope === 'portfolio' ? 'portfolio' : (organizationId ?? ''),
  });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [writtenFeedbackMessageId, setWrittenFeedbackMessageId] = useState<string | null>(null);
  const historySelectSeqRef = useRef(0);

  const notifyAttachmentsNotIncludedThenClear = useCallback(() => {
    if (pendingAttachments.length > 0) {
      toaster.create({
        type: 'info',
        title: 'Only your message was sent',
        description: 'File attachments were not sent with this suggestion.',
      });
    }
    clearPendingAttachments();
  }, [pendingAttachments, clearPendingAttachments]);

  useEffect(() => {
    if (!open) {
      setHistoryOpen(false);
      setWrittenFeedbackMessageId(null);
      historySelectSeqRef.current += 1;
    }
  }, [open]);

  const historyListOrgId = useMemo(
    () => (scope === 'organization' ? organizationId : undefined),
    [scope, organizationId]
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const kinFeedbackSubmitPendingRef = useRef(false);
  const closeKinInlineFeedbackRef = useRef<(() => void) | null>(null);
  const lastAnnouncedRef = useRef<string | null>(null);

  const busy = status === 'submitted' || status === 'streaming';
  const { scrollRootRef, messagesEndRef, showJumpToLatest, scrollToLatest } = useAdvisorMessagesScroll({
    messages,
    busy,
    enabled: open && eligible,
  });

  const prevChatIdRef = useRef(chatId);
  useEffect(() => {
    if (prevChatIdRef.current !== chatId) {
      historySelectSeqRef.current += 1;
      stop();
      setComposerInput('');
      clearPendingAttachments();
      setWrittenFeedbackMessageId(null);
      prevChatIdRef.current = chatId;
    }
  }, [chatId, stop, setComposerInput, clearPendingAttachments]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (kinFeedbackSubmitPendingRef.current) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      const closeInlineFeedback = closeKinInlineFeedbackRef.current;
      if (closeInlineFeedback) {
        e.preventDefault();
        e.stopPropagation();
        closeInlineFeedback();
        return;
      }
      if (historyOpen) {
        e.preventDefault();
        setHistoryOpen(false);
        return;
      }
      setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, setOpen, historyOpen]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [open]);

  const handleSendSuggestion = useCallback(
    (option: { text: string; use_case?: string }) => {
      if (authLoading || !accessToken) return;
      notifyAttachmentsNotIncludedThenClear();
      queueRouteUseCaseForNextStream(option.use_case);
      sendMessage({ text: option.text });
    },
    [sendMessage, authLoading, accessToken, notifyAttachmentsNotIncludedThenClear, queueRouteUseCaseForNextStream]
  );

  const handleAgentRetry = useCallback(() => {
    if (authLoading || !accessToken) return;
    regenerate();
  }, [regenerate, authLoading, accessToken]);

  const handleNewChat = useCallback(() => {
    historySelectSeqRef.current += 1;
    stop();
    setMessages([]);
    setComposerInput('');
    clearPendingAttachments();
    setWrittenFeedbackMessageId(null);
    beginNewAgentThread();
  }, [stop, setMessages, setComposerInput, beginNewAgentThread, clearPendingAttachments]);

  const canUseAgent = !authLoading && !!accessToken;

  const handlePendingReportThreadMessages = useCallback(
    (rows: Parameters<typeof agentV2HistoryRowsToUiMessages>[0]) => {
      setMessages(current =>
        mergeAgentV2HistoryRowsIntoUiMessages(current, rows, {
          threadId: activeThreadIdRef.current ?? undefined,
        })
      );
    },
    [setMessages, activeThreadIdRef]
  );

  const fetchThreadMessagesForPoll = useCallback(
    (threadId: string, useCase: string) =>
      fetchAdminAgentThreadMessages(threadId, {
        organizationId: historyListOrgId,
        useCase,
      }),
    [historyListOrgId]
  );

  useAdvisorPendingReportDeliveryPoll({
    enabled: open && eligible && canUseAgent && Boolean(agentThreadId),
    threadId: agentThreadId,
    threadServerReady,
    resolveUseCase: () => ADMIN_USE_CASE,
    streamIdleToken,
    chatStatus: status,
    fetchThreadMessages: fetchThreadMessagesForPoll,
    onThreadMessages: handlePendingReportThreadMessages,
    onThreadNotFound: clearThreadServerReady,
  });

  const {
    threads: historyThreads,
    loading: historyLoading,
    errorText: historyError,
    deletingThreadId: deletingHistoryThreadId,
    clearError: clearHistoryError,
    reportError: reportHistoryError,
    deleteThread: deleteHistoryThread,
  } = useAdminAgentV2ThreadHistory({
    enabled: historyOpen && canUseAgent,
    organizationId: historyListOrgId,
    formatError: raw => adminAgentFriendlyErrorMessage(raw),
  });

  const handleSelectHistoryThread = useCallback(
    async (row: { id: string; useCase?: string }) => {
      if (!canUseAgent) return;
      const seq = ++historySelectSeqRef.current;
      stop();
      clearHistoryError();
      try {
        const { messages: rows } = await fetchAdminAgentThreadMessages(row.id, {
          organizationId: historyListOrgId,
        });
        if (seq !== historySelectSeqRef.current) return;
        selectPersistedThread(row.id);
        setMessages(agentV2HistoryRowsToUiMessages(rows, { threadId: row.id }));
        setComposerInput('');
        clearPendingAttachments();
        setHistoryOpen(false);
      } catch (err: unknown) {
        if (seq !== historySelectSeqRef.current) return;
        reportHistoryError(err instanceof Error ? err.message : String(err));
      }
    },
    [
      canUseAgent,
      stop,
      selectPersistedThread,
      setMessages,
      setComposerInput,
      historyListOrgId,
      clearHistoryError,
      reportHistoryError,
      clearPendingAttachments,
    ]
  );

  const handleDeleteHistoryThread = useCallback(
    async (row: { id: string; useCase?: string }) => {
      if (!canUseAgent) return;
      clearHistoryError();
      historySelectSeqRef.current += 1;
      try {
        await deleteHistoryThread(row.id, row.useCase);
        historySelectSeqRef.current += 1;
        if (row.id === activeThreadIdRef.current) {
          stop();
          setMessages([]);
          setComposerInput('');
          clearPendingAttachments();
          beginNewAgentThread();
        }
        toaster.create({ type: 'success', title: 'Chat successfully deleted' });
      } catch (err: unknown) {
        toaster.create({
          type: 'error',
          title: adminAgentFriendlyErrorMessage(err instanceof Error ? err.message : String(err)),
        });
      }
    },
    [
      canUseAgent,
      stop,
      setMessages,
      setComposerInput,
      beginNewAgentThread,
      clearHistoryError,
      deleteHistoryThread,
      activeThreadIdRef,
      clearPendingAttachments,
    ]
  );

  const turnFollowUps = useMemo(
    () => (!busy ? getFollowUpsAfterLastUserMessage(messages) : undefined),
    [busy, messages]
  );

  if (!eligible || !open) return null;

  const messageEntries = messages.map(message => ({
    message,
    text: getMessageDisplayText(message),
  }));
  const { visibleMessageEntries, showThinkingIndicator } = resolveAdvisorThreadPresentation(messageEntries, busy);

  let ariaAnnouncement: string | null = null;
  if (showThinkingIndicator) {
    ariaAnnouncement = 'Assistant is preparing a reply';
    lastAnnouncedRef.current = null;
  } else if (error) {
    ariaAnnouncement = 'Advisor encountered an error';
    lastAnnouncedRef.current = null;
  } else if (
    visibleMessageEntries.length > 0 &&
    visibleMessageEntries[visibleMessageEntries.length - 1].message.role === 'assistant' &&
    !busy
  ) {
    const lastMsgId = visibleMessageEntries[visibleMessageEntries.length - 1].message.id;
    if (lastAnnouncedRef.current !== lastMsgId) {
      ariaAnnouncement = 'New response from advisor';
      lastAnnouncedRef.current = lastMsgId;
    }
  }

  const handleHistoryNewChat = () => {
    setHistoryOpen(false);
    handleNewChat();
  };

  if (historyOpen && canUseAgent) {
    return (
      <AgentSidePanelFrame>
        <AgentThreadHistoryView
          topBar={{
            onBack: () => setHistoryOpen(false),
            onNewChat: handleHistoryNewChat,
            newChatDisabled: !canUseAgent,
          }}
          threads={historyThreads}
          loading={historyLoading}
          errorText={historyError}
          onSelectThread={handleSelectHistoryThread}
          onDeleteThread={handleDeleteHistoryThread}
          deletingThreadId={deletingHistoryThreadId}
        />
      </AgentSidePanelFrame>
    );
  }

  return (
    <AgentSidePanelFrame>
      <AssistantHeader
        scope={scope}
        organizationName={organizationName}
        organizationId={organizationId}
        onNewChat={handleNewChat}
        onOpenHistory={() => setHistoryOpen(true)}
        historyDisabled={!canUseAgent}
        onClose={() => setOpen(false)}
      />

      <Box flex={1} minH={0} position="relative" display="flex" flexDirection="column">
        <AgentMessageThreadScroll scrollRootRef={scrollRootRef}>
          {visibleMessageEntries.length === 0 && !error ? (
            <EmptyState
              scope={scope}
              organizationName={organizationName}
              suggestions={suggestionChips.suggestions}
              suggestionsLoading={suggestionChips.isSuggestionsLoading}
              suggestionsError={suggestionChips.isSuggestionsError}
              onRefetchSuggestions={suggestionChips.refetchSuggestions}
              onSendSuggestion={handleSendSuggestion}
              suggestionsDisabled={!canUseAgent}
            />
          ) : (
            visibleMessageEntries.map(({ message }, idx) => {
              if (message.role === 'user') {
                return <AdminUserMessageRow key={message.id} message={message} />;
              }
              const isLast = idx === visibleMessageEntries.length - 1;
              const followUpOptionsOverride =
                isLast && turnFollowUps?.length && !getDataSuggestionsOptions(message)?.length
                  ? turnFollowUps
                  : undefined;
              return (
                <AdminAssistantMessageRow
                  key={message.id}
                  message={message}
                  isLast={isLast}
                  busy={busy}
                  canUseAgent={canUseAgent}
                  onSendSuggestion={handleSendSuggestion}
                  submitMessageFeedback={submitMessageFeedback}
                  setMessages={setMessages}
                  writtenFeedbackMessageId={writtenFeedbackMessageId}
                  setWrittenFeedbackMessageId={setWrittenFeedbackMessageId}
                  kinFeedbackSubmitPendingRef={kinFeedbackSubmitPendingRef}
                  closeKinInlineFeedbackRef={closeKinInlineFeedbackRef}
                  followUpOptionsOverride={followUpOptionsOverride}
                />
              );
            })
          )}

          {showThinkingIndicator ? <AgentThinkingIndicator /> : null}

          {error ? (
            <AgentSystemErrorMessageRow
              message={adminAgentFriendlyErrorMessage(error.message)}
              onRetry={handleAgentRetry}
              showRetry={canUseAgent && messages.length > 0}
            />
          ) : null}

          {!authLoading && !accessToken && (
            <HStack bg="orange.50" px={3} py={2} borderRadius="md" borderWidth="1px" borderColor="orange.100">
              <Text fontSize="xs" color="orange.700">
                Sign in to use Kin.
              </Text>
            </HStack>
          )}

          <div ref={messagesEndRef} />
        </AgentMessageThreadScroll>
        {showJumpToLatest ? <AgentScrollToLatestPill onClick={scrollToLatest} /> : null}
        <div
          aria-live="polite"
          aria-atomic="true"
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            border: 0,
            clipPath: 'inset(50%)',
          }}
        >
          {ariaAnnouncement}
        </div>
      </Box>

      <AgentComposeBar
        input={input}
        setInput={setComposerInput}
        onSend={() => {
          const trimmed = input.trim();
          if ((!trimmed && pendingAttachments.length === 0) || authLoading || !accessToken) return;
          const attachmentsToSend = pendingAttachments.slice();
          const seq = (sendSeqRef.current += 1);
          pendingSendRef.current = { seq, text: trimmed, files: attachmentsToSend };
          setComposerInput('');
          if (attachmentsToSend.length > 0) {
            clearPendingAttachments();
          }
          if (attachmentsToSend.length > 0) {
            void sendMessage({
              text: trimmed,
              files: fileListFromFiles(attachmentsToSend),
            });
          } else {
            void sendMessage({ text: trimmed });
            pendingSendRef.current = null;
          }
        }}
        busy={busy}
        disabled={!canUseAgent}
        onStop={stop}
        textareaRef={textareaRef}
        accentHex={KIN_ACCENT}
        uploadIconSrc={KIN_UPLOAD_URL}
        sendIconSrc={KIN_SEND_URL}
        composerAriaLabel="Ask Kin"
        sendAriaLabel="Send message to Kin"
        attachments={{
          files: pendingAttachments,
          onAddFiles: addPendingAttachments,
          onRemoveAt: removePendingAttachment,
          isAtLimit: isPendingAttachmentLimit,
          errorMessages: pendingAttachmentErrors,
          onClearErrors: clearPendingAttachmentErrors,
        }}
      />
    </AgentSidePanelFrame>
  );
}
